import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { checkDrivingLicence, createAttachment, getBundleStatusAsync, getDossiersByOwner, getDossiersTemplates, readAttachmentPageAsync, updateAttachment, updateAttachmentStatus, updateDossierStatus } from "../../api/service/dossierApi";
import { BundleStatusResponse, CBDossierKindKeys, CBPartnerType, CB_BUNDLE_STEP_DOSSIER_KIND_MAP, DOSSIER_ATTACHMENT_FIELD_SORT_ORDER, DossierDto, DossierTemplateDto, ICreateAttachmentParams, IDossierFormValues, IFormValues, ITempAttachmentPage, IUpdateDocumentStatusRequest, Page } from "../../types";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { attachmentCreateQueryFieldsState, attachmentUpdateQueryFieldsState, checkDrivingLicenceQueryFieldsState, updateAttachmentStatusQueryFieldsState, updateDossierStatusQueryFieldsState,  } from "../../state/customerbaseState";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { useTranslation } from "react-i18next";
import CustomLoader from "../../../../util/component/CustomLoader";
import CustomerBaseDossiersPage from "./CustomerBaseDossiersPage";
import { getErrorKey } from "../../utils";
import { useGetCustomerByIdMetaQueryObserver } from "../../api/service/customerbaseApi";

const CustomerBaseDossiersTab = () => {
    const { dispatchGlobalData } = useContext(GlobalContext);
    const { id } = useParams();
    const { t } = useTranslation("customerbase");
    const formContext = useFormContext<IFormValues>();
    const dossierForm = useForm<IDossierFormValues>({
        defaultValues: {
            attachments: []
        }
    });
    const [templates, setTemplates] = useState<DossierTemplateDto[]>([]);
    const [dossierTemplateIdentifier, setDossierTemplateIdentifier] = useState('');
    const attachmentCreateQueryFields = useRecoilValue(attachmentCreateQueryFieldsState);
    const setAttachmentCreateQueryFields = useSetRecoilState(attachmentCreateQueryFieldsState);
    const attachmentUpdateQueryFields = useRecoilValue(attachmentUpdateQueryFieldsState);
    const setAttachmentUpdateQueryFields = useSetRecoilState(attachmentUpdateQueryFieldsState);
    const checkDrivingLicenceQueryFields = useRecoilValue(checkDrivingLicenceQueryFieldsState);
    const setCheckDrivingLicenceQueryFields = useSetRecoilState(checkDrivingLicenceQueryFieldsState);
    const updateAttachmentStatusQueryFields = useRecoilValue(updateAttachmentStatusQueryFieldsState);
    const setUpdateAttachmentStatusQueryFields = useSetRecoilState(updateAttachmentStatusQueryFieldsState);
    const updateDossierStatusQueryFields = useRecoilValue(updateDossierStatusQueryFieldsState);
    const setUpdateDossierStatusQueryFields = useSetRecoilState(updateDossierStatusQueryFieldsState);
    const [longFetching, setLongFetching] = useState(false);

    const getCustomerQuery = useGetCustomerByIdMetaQueryObserver(id);

    const updateAttachmentStatusDefaultValue = (identifier: string, status: string) => {
        const attachs: any[] = dossierForm.getValues("attachments");
        const index = attachs.findIndex((att: any) => att.identifier == identifier);
        if(index != -1 && !!status)
            dossierForm.resetField(`attachments.${index}.status`, {defaultValue: status});
    }

    const updateAttachmentQuery = useMutation({
        mutationFn: () => {
            return updateAttachment(attachmentUpdateQueryFields);
        },
        onSuccess: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.success.default"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setAttachmentUpdateQueryFields(undefined);
            refetch();
        },
        onError: (err: any) => {
            console.log("Err: ", err);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`message.error.${getErrorKey(err)}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setAttachmentUpdateQueryFields(undefined);
        }
    });

    useEffect(() => {
        if(!!attachmentUpdateQueryFields)
            updateAttachmentQuery.mutate();

    }, [attachmentUpdateQueryFields]);

    const createAttachmentQuery = useMutation({
        mutationFn: (params: ICreateAttachmentParams) => {
            return createAttachment(params);
        },
        onSuccess: (data: any) => {
            console.log("Data: ", data?.data);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.success.default"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setAttachmentCreateQueryFields(undefined);

            refetch();
        },
        onError: (err: any) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`message.error.${getErrorKey(err)}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setAttachmentCreateQueryFields(undefined);
        }
        
    });

    useEffect(() => {
        if(!!attachmentCreateQueryFields)
        createAttachmentQuery.mutate(attachmentCreateQueryFields);

    }, [attachmentCreateQueryFields]);

    const checkDrivingLicenceQuery = useQuery(['checkDrivingLicenceQuery', checkDrivingLicenceQueryFields], checkDrivingLicence, 
    {
        enabled: false,
        onSuccess: (data: any) => {
            console.log("Check DL: ", data?.data);

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.success.default"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setCheckDrivingLicenceQueryFields(undefined);

            refetch();
        },
        onError: (err: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`message.error.${getErrorKey(err)}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setCheckDrivingLicenceQueryFields(undefined);
        }
    });

    useEffect(() => {
        if(!!checkDrivingLicenceQueryFields) 
            checkDrivingLicenceQuery.refetch();

    }, [checkDrivingLicenceQueryFields]);

    const updateAttachmentStatusQuery = useMutation({
        mutationFn: (params: IUpdateDocumentStatusRequest) => {
            return updateAttachmentStatus(params);
        },
        onSuccess: (data: any) => {
            console.log("OnSuccess Update Attachment Status: ", data?.data);

            const attach = data?.data?.attachment;

            if(!attach) return; 

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.success.default"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            updateAttachmentStatusDefaultValue(attach.identifier, attach.status);

            setUpdateAttachmentStatusQueryFields(undefined);

            //refetch();
        },
        onError: (err: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`message.error.${getErrorKey(err)}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setUpdateAttachmentStatusQueryFields(undefined);
        }


    });


    useEffect(() => {
        if(!!updateAttachmentStatusQueryFields)
            updateAttachmentStatusQuery.mutate(updateAttachmentStatusQueryFields);

    }, [updateAttachmentStatusQueryFields]);


    const updateDossierStatusQuery = useMutation({
        mutationFn: (params: IUpdateDocumentStatusRequest) => {
            return updateDossierStatus(params);
        },
        onSuccess: (data: any) => {
            console.log("OnSuccess Update Dossier Status: ", data?.data);

            const dossier = data?.data?.dossier;

            if(!dossier || !dossier.status) return; 

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.success.default"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            dossierForm.resetField("status", {defaultValue: dossier.status});

            setUpdateDossierStatusQueryFields(undefined);
            
            getCustomerQuery.refetch();
            
            //refetch();
        },
        onError: (err: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`message.error.${getErrorKey(err)}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setUpdateDossierStatusQueryFields(undefined);
        }
    });

    useEffect(() => {
        if(!!updateDossierStatusQueryFields)
            updateDossierStatusQuery.mutate(updateDossierStatusQueryFields);

    }, [updateDossierStatusQueryFields]);

    const getDossierTemplatesQuery = useQuery(['getDossiersTemplates'], getDossiersTemplates,
    {
        enabled: false,
        onSuccess: (data: any) => {
            console.log("Templates: ", data);
            //prendi templates
           setTemplates(data?.data?.templates || []);
           getCustomerQuery.refetch();
           setLongFetching(false);
           
        },
        onError: (err: any) => {
            setLongFetching(false);
            /*dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("message.error.default"),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });*/
        }

    });

    const { refetch } = useQuery(['getDossiersByOwner', id],
    {
        enabled: !!id,
        queryFn: () => {
            if(!!id)
                return getDossiersByOwner(id);
        },
        onSuccess: async (data: any) => {
            console.log("Dossier: ", data?.data);
            
            const dossier: DossierDto = data?.data?.dossiers[0];

            if(!dossier) {
                setLongFetching(false);
                return;
            }
            setLongFetching(true);

            let currentPages: Page[] = [];
            dossier.attachments.forEach(({pages}) => {
                pages.forEach(({owner, dossier, attachment, type}) => {
                    currentPages.push({
                        attachment: attachment,
                        creationTime: "",
                        dossier: dossier,
                        note: "",
                        origin: "",
                        originIdentifier: "",
                        owner: owner,
                        status: "",
                        type: type,
                        updateTime: ""
                    })
                })
            });

            console.log("CurrentPages: ", currentPages);

            let promises: any[] = [];

            currentPages.forEach(({owner, dossier, attachment, type}) => {
                promises.push(readAttachmentPageAsync(owner, dossier, attachment, type));

            });

            let tempAttachmentPages: ITempAttachmentPage[] = [];

            await Promise.allSettled(promises).then((results) => {
                tempAttachmentPages = results.map((result, index) => ({
                    identifier: currentPages[index].attachment || '',
                    type: currentPages[index].type || '',
                    frontPage: result.status == "fulfilled" ? result.value : null,
                    backPage: result.status == "fulfilled" ? result.value : null
                }));

            });

            const bundles = formContext.getValues("cbBundles");
            var bundleStatus: BundleStatusResponse | null = null;
            if(bundles?.length > 0 && !!bundles[0].uuid) {
                bundleStatus = await getBundleStatusAsync(bundles[0].uuid).catch((reason) => {console.log(reason)});
            }

            dossierForm.reset({
                owner: dossier.owner,
                identifier : dossier.identifier,
                template: dossier.template,
                status: dossier.status,
                description: dossier.description,
                title: dossier.title,
                note: dossier.note,
                origin: dossier.origin,
                originIdentifier: dossier.originIdentifier,
                attachments: dossier.attachments?.map(att => ({
                    owner: att.owner,
                    dossier: att.dossier,
                    identifier: att.identifier,
                    number: att.number,
                    type: {
                        identifier: att.type.identifier,
                        title: att.type.title,
                        description: att.type.description,
                        kind: att.type.kind,
                        fields: att.type.fields.map(f => ({
                            title: f.title,
                            description: f.description,
                            name: f.name,
                            type: f.type,
                            values: [...f.values] || [],
                            tags: [...f.tags] || []
                        })) || [],
                        pages: [...att.type.pages],
                        tags: [...att.type.tags]
                    },
                    numOfTypePages: att.type.pages.length || 2,
                    status: att.status,
                    issueDate: att.issueDate,
                    expireDate: att.expireDate,
                    origin: att.origin,
                    originIdentifier: att.originIdentifier,
                    creationTime: Number(att.creationTime || 0),
                    updateTime: Number(att.updateTime || 0),
                    country: att.country,
                    frontPage: tempAttachmentPages.filter(tap => tap.identifier == att.identifier).find(p => (p.type == "FRONT_PAGE") || (p.type == "SELFIE_PAGE"))?.frontPage || null,
                    backPage: tempAttachmentPages.filter(tap => tap.identifier == att.identifier).find(p => p.type == "BACK_PAGE")?.backPage || null,
                    pages: att.pages?.map(page => ({
                        owner: page.owner,
                        dossier: page.dossier,
                        attachment: page.attachment,
                        type: page.type,
                        status: page.status,
                        creationTime: Number(page.creationTime),
                        updateTime: Number(page.updateTime)
                    })) || [],
                    fields: [...att.type.fields].sort((a,b) => DOSSIER_ATTACHMENT_FIELD_SORT_ORDER.indexOf(a.name || '') - DOSSIER_ATTACHMENT_FIELD_SORT_ORDER.indexOf(b.name || '')).map(configField => ({
                        owner: att.owner,
                        dossier: att.dossier,
                        attachment: att.identifier,
                        type: {
                            title: configField.title,
                            name: configField.name,
                            description: configField.description,
                            type: configField.type,
                            values: [...configField.values],
                            tags: [...configField.tags]
                        },
                        value: att.fields?.find(field => field.type.name == configField.name)?.value || null,
                        values: att.fields.find(field => field.type.name == configField.name) == undefined ? [] : [...att.fields.find(field => field.type.name == configField.name)?.values || []]
                        })) || [],
                        bundleInfo: !!bundleStatus && (att.type?.identifier !== CBDossierKindKeys.SELFIE) ? bundleStatus.data?.find(el => CB_BUNDLE_STEP_DOSSIER_KIND_MAP.find(m => m.key == el.step?.toUpperCase())?.value == att.type?.identifier) || null : null,   
                })) || []
            });            

            setDossierTemplateIdentifier(dossier.template || '');

            getDossierTemplatesQuery.refetch();
    

        },
        onError: (err: any) => {
            //se codice errore è  not exist dossier of owner
            const errorMessage: string = err.response?.data?.errorMessage || '';
            if(!!errorMessage && errorMessage.includes("500") && errorMessage.includes("listByOwner NOT existing person"))
                getDossierTemplatesQuery.refetch();
            
            
            /*else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("message.error.default"),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }*/    

        }
    });

    return (   
        <>
            <FormProvider {...dossierForm}>
                <CustomerBaseDossiersPage
                    templates={templates}
                    dossierTemplateIdentifier={dossierTemplateIdentifier}
                    isOrganization={formContext.getValues("cbDetails.partnerType") == CBPartnerType.ORGANIZATION}
                    cbId={formContext.getValues("cbDetails.cbCustomerId")}
                    cbFullName={formContext.getValues("cbPersonalInfo.firstName") + " " + formContext.getValues("cbPersonalInfo.lastName")}
                    //dossier={dossier}
                    isLoading={templates.length==0 || longFetching}
                />
            </FormProvider>
            <CustomLoader loading={createAttachmentQuery.isLoading || updateAttachmentQuery.isLoading || checkDrivingLicenceQuery.isFetching || updateAttachmentStatusQuery.isLoading || updateDossierStatusQuery.isLoading /*|| (templates.length!==0 && isFetching)*/}/>
        </>

        
    )
}

export default CustomerBaseDossiersTab;